import { useTheme, useToastProvider } from "@deere/fuel-react";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, Typography } from "@mui/material";
import { GridSortModel } from "@mui/x-data-grid-pro";
import { useOktaAuth } from "@okta/okta-react";
import dayjs from "dayjs";
import { useContext, useEffect, useMemo, useReducer } from "react";
import { useTranslation } from "react-i18next";
import D2DOrdersTableV2 from "./components/D2DOrdersTableV2";
import {
  setCurrentPageAction,
  setD2DOrdersAction,
  setFilterAction,
  setIsDownloadingAction,
  setIsLoadingAction,
  setIsTechnicalErrorAction,
  setPageRowSizeAction,
  setSortModelAction,
} from "./reducers/actions";
import { d2dOrdersReducer, getInitialState } from "./reducers/reducers";
import { D2DOrdersFilters } from "./types";
import { sortD2DOrdersDefault } from "./utils/defaultSort";
import {
  downloadD2DOrdersAsCSV,
  getTooltipTextForDownloadButton,
} from "./utils/downloadUtils";
import { groupD2DOrders } from "./utils/orderGrouping";
import d2dApi from "../../../api/d2d/d2dApi";
import Pagination from "../../../components/DataGridPro/Pagination/Pagination";
import ErrorPage from "../../../components/ErrorPage/ErrorPage";
import StickyFooter from "../../../components/StickyFooter/StickyFooter";
import { useConfigContext } from "../../../context/ConfigContext";
import { DealerAccountContext } from "../../../context/DealerAccountContext";
import { getD2DRejectStatusLabelKey } from "../utils/rejectionStatus";

function D2DOrders() {
  const { t } = useTranslation(["d2d", "common"]);
  const theme = useTheme();
  const { authState } = useOktaAuth();
  const { dealerAccount } = useContext(DealerAccountContext);
  const { D2D_INVENTORY_API_URL } = useConfigContext();
  const { enqueueToast } = useToastProvider();

  const [state, dispatch] = useReducer(
    d2dOrdersReducer,
    getInitialState(dealerAccount?.id ?? "")
  );

  const token = authState?.accessToken?.accessToken || "";
  const isAuthorized =
    !!token && !!dealerAccount?.id && !!D2D_INVENTORY_API_URL;

  useEffect(() => {
    if (isAuthorized) {
      fetchD2DOrders();
    }
  }, [
    isAuthorized,
    dealerAccount?.id,
    token,
    state.currentPage,
    state.pageRowSize,
    state.sortModel,
  ]);

  const fetchD2DOrders = async () => {
    dispatch(setIsLoadingAction(true));
    dispatch(setIsTechnicalErrorAction(false));

    try {
      const response = await d2dApi.fetchD2DBuyerOrders(
        D2D_INVENTORY_API_URL,
        token,
        dealerAccount?.id as string
      );

      const rawOrders = response.data.d2dOrders || [];
      const groupedOrders = groupD2DOrders(rawOrders);

      dispatch(
        setD2DOrdersAction({
          d2dOrders: groupedOrders,
          totalOrders: groupedOrders.length,
        })
      );
    } catch (error: any) {
      console.error("Error fetching D2D buyer orders:", error);
      dispatch(setIsTechnicalErrorAction(true));
      enqueueToast({
        variant: "error",
        message: t("common:technicalError.subtitle"),
        isCloseable: true,
      });
    } finally {
      dispatch(setIsLoadingAction(false));
    }
  };

  const filteredOrders = useMemo(() => {
    let filtered = state.d2dOrders;

    if (state.filters.orderNumber) {
      filtered = filtered.filter((order) =>
        order.orderNumber
          ?.toLowerCase()
          .includes(state.filters.orderNumber!.toLowerCase())
      );
    }
    if (state.filters.dealerReference) {
      filtered = filtered.filter((order) =>
        order.dealerReference
          ?.toLowerCase()
          .includes(state.filters.dealerReference!.toLowerCase())
      );
    }
    if (state.filters.partNumber) {
      filtered = filtered.filter((order) =>
        order.partNumber
          ?.toLowerCase()
          .includes(state.filters.partNumber!.toLowerCase())
      );
    }
    if (state.filters.sellerAccountId) {
      filtered = filtered.filter((order) =>
        order.sellerAccountId
          ?.toLowerCase()
          .includes(state.filters.sellerAccountId!.toLowerCase())
      );
    }
    if (state.filters.carrierName) {
      filtered = filtered.filter((order) =>
        order.carrierName
          ?.toLowerCase()
          .includes(state.filters.carrierName!.toLowerCase())
      );
    }
    if (state.filters.trackingNumber) {
      filtered = filtered.filter((order) =>
        order.trackingNumber
          ?.toLowerCase()
          .includes(state.filters.trackingNumber!.toLowerCase())
      );
    }

    if (state.filters.statuses?.length) {
      const selected = state.filters.statuses as string[];
      const wantsAuto = selected.includes("D2D_CLAIMED");
      const wantsManual = selected.includes("MANUAL_REJECTED");
      filtered = filtered.filter((order) => {
        if (selected.includes(order.status)) return true;
        if (order.status !== "REJECTED" || (!wantsAuto && !wantsManual))
          return false;
        const key = getD2DRejectStatusLabelKey({
          status: order.status,
          lastChangedBy: order.lastChangedBy,
          rejectionType: order.rejectionType,
          rejectionTrigger: order.rejectionTrigger,
        });
        return (
          (wantsAuto && key === "d2d:status.d2dClaimed") ||
          (wantsManual && key === "d2d:status.manualRejected")
        );
      });
    }

    if (state.filters.dateRequestedFrom && state.filters.dateRequestedTo) {
      filtered = filtered.filter((order) => {
        if (!order.dateRequested) return false;
        const orderDate = dayjs(order.dateRequested);
        return (
          (orderDate.isAfter(state.filters.dateRequestedFrom, "day") ||
            orderDate.isSame(state.filters.dateRequestedFrom, "day")) &&
          (orderDate.isBefore(state.filters.dateRequestedTo, "day") ||
            orderDate.isSame(state.filters.dateRequestedTo, "day"))
        );
      });
    }

    return filtered;
  }, [state.d2dOrders, state.filters]);

  const sortedOrders = useMemo(() => {
    if (!state.sortModel || state.sortModel.length === 0) {
      return sortD2DOrdersDefault(filteredOrders);
    }

    const { field, sort } = state.sortModel[0];
    const sorted = [...filteredOrders].sort((a, b) => {
      const aValue = a[field as keyof typeof a];
      const bValue = b[field as keyof typeof b];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (field === "dateRequested") {
        const aDate = dayjs(aValue as string);
        const bDate = dayjs(bValue as string);
        if (sort === "asc") return aDate.isBefore(bDate) ? -1 : 1;
        return aDate.isAfter(bDate) ? -1 : 1;
      }

      if (field === "requestedQuantity" || field === "acceptedQuantity") {
        const aNum = Number(aValue);
        const bNum = Number(bValue);
        if (sort === "asc") return aNum - bNum;
        return bNum - aNum;
      }

      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      if (sort === "asc") return aStr.localeCompare(bStr);
      return bStr.localeCompare(aStr);
    });

    return sorted;
  }, [filteredOrders, state.sortModel]);

  const paginatedOrders = useMemo(() => {
    const startIndex = (state.currentPage - 1) * state.pageRowSize;
    const endIndex = startIndex + state.pageRowSize;
    return sortedOrders.slice(startIndex, endIndex);
  }, [sortedOrders, state.currentPage, state.pageRowSize]);

  const totalFilteredOrders = filteredOrders.length;

  const isFilterApplied = useMemo(
    () =>
      !!(
        state.filters.orderNumber ||
        state.filters.dealerReference ||
        state.filters.partNumber ||
        state.filters.sellerAccountId ||
        state.filters.trackingNumber ||
        (state.filters.statuses && state.filters.statuses.length > 0) ||
        (state.filters.dateRequestedFrom && state.filters.dateRequestedTo)
      ),
    [state.filters]
  );

  const handleClearFilters = () => {
    const emptyFilters: D2DOrdersFilters = {
      orderNumber: undefined,
      dealerReference: undefined,
      partNumber: undefined,
      sellerAccountId: undefined,
      carrierName: undefined,
      trackingNumber: undefined,
      status: undefined,
      statuses: [],
      dateRequestedFrom: null,
      dateRequestedTo: null,
    };
    dispatch(setFilterAction(emptyFilters));
    dispatch(setCurrentPageAction(1));
  };

  const handlePageChange = (page: number) => {
    dispatch(setCurrentPageAction(page));
  };

  const handleRowsPerPageChange = (pageSize: number) => {
    dispatch(setPageRowSizeAction(pageSize));
    dispatch(setCurrentPageAction(1));
  };

  const handleSortModelChange = (model: GridSortModel) => {
    dispatch(setSortModelAction(model));
  };

  const handleDownload = () => {
    dispatch(setIsDownloadingAction(true));
    try {
      downloadD2DOrdersAsCSV(sortedOrders, t);
      enqueueToast({
        variant: "success",
        message: t("common:table.download.success"),
        isCloseable: true,
      });
    } catch (error) {
      console.error("Error downloading D2D orders:", error);
      enqueueToast({
        variant: "error",
        message: t("common:table.download.error"),
        isCloseable: true,
      });
    } finally {
      dispatch(setIsDownloadingAction(false));
    }
  };

  if (!isAuthorized) {
    return (
      <Box sx={{ display: "flex", height: "70vh", justifyContent: "center" }}>
        <ErrorPage
          title={t("common:authentication.not.mapped.account.subtitle")}
          subtitles={[t("common:authentication.failed.subtitle")]}
          icon={{ Component: WarningAmberIcon, color: "warning" }}
          showBackToHomeLink={false}
          showLoginBtn={false}
          showReturnHomeButton
        />
      </Box>
    );
  }

  const showPagination = !state.isLoading && totalFilteredOrders > 0;

  return (
    <>
      <Box>
        <Typography
          component="h1"
          data-fuel-size="3xl"
          fontWeight="semibold"
          sx={{
            paddingBottom: theme.tokens.FuelSpacing6,
            color: theme.tokens.FuelColorTextPrimary,
          }}
          data-testid="d2d-orders-title"
        >
          {t("d2d:d2dOrders.title")}
        </Typography>
        <Typography
          component="h3"
          sx={{
            fontWeight: theme.tokens.FuelFontWeightSemibold,
            fontSize: theme.tokens.FuelFontSizeHeading3,
            marginBottom: theme.tokens.FuelSpacing4,
            lineHeight: theme.tokens.FuelLineHeightLg,
          }}
          color={theme.tokens.FuelColorTextPrimary}
          data-testid="d2d-orders-count"
        >
          {t("d2d:d2dOrders.totalOrders", {
            count: totalFilteredOrders,
          })}
        </Typography>

        <D2DOrdersTableV2
          orders={paginatedOrders.map((order, index) => ({
            ...order,
            id: order.id || `order-${index}`,
            accountId: dealerAccount?.id,
          }))}
          loading={state.isLoading}
          showTechnicalError={state.isTechnicalError}
          sort={{
            sortModel: state.sortModel,
            updateSortModel: handleSortModelChange,
          }}
          filter={{
            d2dOrdersFilters: state.filters,
            isFilterApplied,
            onClearFilters: handleClearFilters,
          }}
          download={{
            isDownloading: state.isDownloading,
            onDownload: handleDownload,
            tooltipText: getTooltipTextForDownloadButton(
              sortedOrders,
              isFilterApplied,
              state.isDownloading,
              t
            ),
          }}
          dispatch={dispatch}
        />
      </Box>
      {showPagination && (
        <StickyFooter>
          <Pagination
            rowsPerPage={state.pageRowSize}
            currentPage={state.currentPage}
            totalCount={totalFilteredOrders}
            onRowsPerPageChange={handleRowsPerPageChange}
            onFirstPageClick={() => handlePageChange(1)}
            onPreviousClick={() =>
              handlePageChange(Math.max(1, state.currentPage - 1))
            }
            onLastPageClick={() =>
              handlePageChange(
                Math.ceil(totalFilteredOrders / state.pageRowSize)
              )
            }
            onNextClick={() =>
              handlePageChange(
                Math.min(
                  Math.ceil(totalFilteredOrders / state.pageRowSize),
                  state.currentPage + 1
                )
              )
            }
          />
        </StickyFooter>
      )}
    </>
  );
}

export default D2DOrders;
